"use client";
import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import SearchIcon from "../SVG/SearchIcon";
import Button from "../ui/Button";

export type SearchDrawerProps = {
  open: boolean;
  onClose: () => void;

  // Context
  categoryLabel: string;
  showCategoryContext?: boolean;

  // Query controls
  initialQuery: string;
  onSubmit: (q: string) => void;
  onReset: () => void;
};

export default function SearchDrawer({
  open,
  onClose,
  categoryLabel,
  showCategoryContext = true,
  initialQuery,
  onSubmit,
  onReset,
}: SearchDrawerProps) {
  const [localQuery, setLocalQuery] = useState(initialQuery);

  // Reset the query to match the latest prop whenever the drawer transitions to open
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setLocalQuery(initialQuery);
  }

  const handleSubmit = useCallback(() => {
    onSubmit(localQuery.trim());
    onClose();
  }, [localQuery, onSubmit, onClose]);

  // Body scroll lock and ESC close
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, handleSubmit]);

  return (
    <div className={`fixed inset-0 z-102 md:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}>
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />

      {/* Panel (full width) */}
      <div
        className={`absolute inset-y-0 right-0 left-0 h-full w-full bg-white shadow-xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-search-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 py-4">
          <div className="flex items-center gap-2.5">
            <SearchIcon className="w-6 h-6" circleColor="#40BFDE" strokeColor="#ffffff" />
            <h3 id="mobile-search-title" className="text-[#1B365E] text-[30px] leading-10 font-light font-red-hat">Cherchez</h3>
          </div>
          <div className="flex items-center gap-6">
            <button aria-label="reset" onClick={() => { onReset(); setLocalQuery(""); }}>
              <Image src="/assets/icons/reload.svg" alt="reset" width={24} height={24} />
            </button>
            <button aria-label="close" onClick={onClose}>
              <Image src="/assets/icons/icon-close.svg" alt="close" width={24} height={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pt-2 pb-4 flex flex-col gap-6 text-[#1D1D1D] overflow-y-auto h-[calc(100%-120px)]">
          {/* Category context */}
          {showCategoryContext && (
            <div className="flex items-center gap-x-6 gap-y-4 flex-wrap">
              <p className="text-[#1D1D1D] font-bold text-[15px] leading-4.5">Dans la catégorie :</p>
              <div className="inline-flex items-center gap-3 bg-[#1B365E] text-white rounded-full px-4 py-2 text-[8px] leading-2.5 font-bold">
                <span className="inline-flex items-center justify-center text-[8px] scale-[160%] font-normal">×</span>
                <span>{categoryLabel}</span>
              </div>
            </div>
          )}

          {/* Search input */}
          <div className="px-1 py-6 border-y border-[#EFEFEF]">
            <div className="flex items-center gap-2 bg-white border border-[#EFEFEF] focus-within:border-[#40BFDE] rounded-lg px-3 py-2">
              <input
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder="Rechercher une formation..."
                className="flex-1 outline-none text-sm text-[#1B365E] placeholder:text-[#ADADAD] bg-transparent"
              />
              <button className="w-6 shrink-0" aria-label="submit-search" onClick={handleSubmit}>
                <SearchIcon className="w-6 h-6" circleColor="#1B365E" strokeColor="#ffffff" />
              </button>
            </div>
          </div>

          {/* Footer action */}
          <Button
            variant="primary"
            className='w-full text-[15px] font-bold h-9! py-1 px-4 border border-[#EFEFEF] mt-auto'
            onClick={handleSubmit}
          >
            OK
          </Button>
        </div>
      </div>
    </div>
  );
}
